import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import Map, { MapRef } from 'react-map-gl';
import { useSnapshot } from 'valtio';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Checkbox } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { FormGroup } from '@mui/material';
import { Radio } from '@mui/material';
import { RadioGroup } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import LayersIcon from '@mui/icons-material/Layers';
import MapIcon from '@mui/icons-material/Map';

import { store } from '../../store';
import { toggleGeofenceType } from '../../store/mapLayers';
import { DeckGlLayers } from '../Deckgl';

// import ToggleButton from '@mui/material/ToggleButton';
import { TripFooter } from './TripFooter';

import 'mapbox-gl/dist/mapbox-gl.css';

const TYPE_COLORS: Record<string, string> = {
  REEFER_STORAGE: 'rgba(0,255,0,1)',
  DRY_STORAGE: 'rgba(255,165,0,1)',
  CUSTOM: 'rgba(255,0,0,1)',
  PHYTO: 'rgba(255,255,0,1)',
  RAIL_TERMINAL: 'rgba(128,0,128,1)',
  LOADING_QUAY: 'rgba(0,255,255,1)',
};

const GEOFENCE_TYPE_LABELS: Record<string, string> = {
  DRY_STORAGE: 'Dry storage',
  REEFER_STORAGE: 'Reefer storage',
  CUSTOM: 'Custom',
  PHYTO: 'Phyto',
  RAIL_TERMINAL: 'Rail terminal',
  LOADING_QUAY: 'Loading quay',
};

export const InteractiveMap = ({
  mapRef,
}: {
  mapRef: React.MutableRefObject<MapRef | null>;
}) => {
  const snap = useSnapshot(store);
  const { mapLayers } = snap;
  const { t } = useTranslation();

  const [mapBackground, setMapBackground] = useState('satellite-streets-v12');
  const [isMapTypeOpen, setIsMapTypeOpen] = useState(false);
  const [isLayersOpen, setIsLayersOpen] = useState(false);

  const handleMapTypeClick = () => {
    setIsLayersOpen(false);
    setIsMapTypeOpen(!isMapTypeOpen);
  };

  const handleLayersClick = () => {
    setIsMapTypeOpen(false);
    setIsLayersOpen(!isLayersOpen);
  };

  const handleLayerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    // @ts-ignore – dynamic key access
    if (store.mapLayers[name]) {
      // @ts-ignore
      store.mapLayers[name].display = checked;
    }
  };

  return (
    <Map
      ref={mapRef}
      mapboxAccessToken="pk.eyJ1IjoicmVlZmVycHVsc2UiLCJhIjoiY2xuOHluNGh1MDJtMDJqdDlzb3A3cW1qbSJ9.juaBfcJcCyVfZQ0sTB7zTg"
      mapStyle={`mapbox://styles/mapbox/${mapBackground}`}
      initialViewState={{
        longitude: 18.54009,
        latitude: -33.80737,
        zoom: 10,
      }}
      cursor="pointer"
      style={{
        width: '100%',
        height: '100%',

        position: 'sticky',
      }}
    >
      {/* Toolbar - Verticale en haut à droite */}
      <Box
        sx={{
          position: 'absolute',
          right: 10,
          top: 10,
          backgroundColor: 'white',
          borderRadius: 1,
          padding: 1,
          boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column', // Retour en vertical
          flex: 1,
          gap: 1,
          zIndex: 1000,
        }}
      >
        <Tooltip title="Map type" placement="left">
          <IconButton
            onClick={handleMapTypeClick}
            color={isMapTypeOpen ? 'primary' : 'default'}
          >
            <MapIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Layers" placement="left">
          <IconButton
            onClick={handleLayersClick}
            color={isLayersOpen ? 'primary' : 'default'}
          >
            <LayersIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Map Type Popup - À gauche de la toolbar */}
      {isMapTypeOpen && (
        <Box
          sx={{
            position: 'absolute',
            right: 65, // Ajusté pour être juste à gauche de la toolbar
            top: 10,
            backgroundColor: 'white',
            borderRadius: 1,
            padding: 2,
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
            width: 200,
            // zIndex: 1000,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Map type</Typography>
            <IconButton
              size="small"
              onClick={handleMapTypeClick}
              sx={{ padding: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <RadioGroup
            value={mapBackground}
            onChange={(e) => setMapBackground(e.target.value)}
            aria-label="map-type"
          >
            <FormControlLabel
              value="streets-v9"
              control={<Radio size="small" />}
              label="Light map"
            />
            <FormControlLabel
              value="satellite-streets-v12"
              control={<Radio size="small" />}
              label="Satellite map"
            />
          </RadioGroup>
        </Box>
      )}

      {isLayersOpen && (
        <Box
          sx={{
            position: 'absolute',
            right: 65,
            top: 10,
            backgroundColor: 'white',
            borderRadius: 1,
            padding: 2,
            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
            width: 200,
            zIndex: 1000,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1">Layers</Typography>
            <IconButton
              size="small"
              onClick={handleLayersClick}
              sx={{ padding: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={mapLayers.berths.display}
                  onChange={handleLayerChange}
                  name="berths"
                />
              }
              label={t('layers.berth', { defaultValue: 'Berth' })}
            />

            {Object.keys(GEOFENCE_TYPE_LABELS).map((type) => (
              <FormControlLabel
                key={type}
                control={
                  <Checkbox
                    size="small"
                    checked={mapLayers.geofences.selectedTypes.includes(type)}
                    onChange={() => toggleGeofenceType(type)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: TYPE_COLORS[type],
                      }}
                    />
                    {t(`layers.${type.toLowerCase()}`, {
                      defaultValue: GEOFENCE_TYPE_LABELS[type],
                    })}
                  </Box>
                }
              />
            ))}
          </FormGroup>
        </Box>
      )}

      <DeckGlLayers mapRef={mapRef} />
      <TripFooter />
    </Map>
  );
};
